import { ReactNode, useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Layout } from '@/components/Layout/Layout';
import { Footer } from '@/components/Footer';
import {
  buildBrandContact,
  getBrandFavicon,
  getBrandLogo,
} from '@/helpers/brandLogo';
import { useFavicon } from '@/hooks/useFavicon';
import {
  ApiReport,
  OrderStatusWithVehicles,
  UserVehicle,
  isOrderCanceled,
  isOrderSuspended,
  shouldShowPaymentBanner,
  canChangeVehicle,
} from '@/api/types/models.types';
import { reportService } from '@/api/services/report.service';
import { mapOrderStatusRA2ToReport } from '@/api/mappers/order-status.mapper';
import { mapOrderTimelineToStatus } from '@/api/mappers/timeline.mapper';
import OrderHeader from '@/components/OrderHeader/OrderHeader';
import { OrderTimeline } from '@/components/OrderTimeline/OrderTimeline';
import { OrderBody } from '@/components/OrderBody/OrderBody';
import { OrderVehicles } from '@/components/OrderVehicles';
import { ChangeVehicleAlert } from '@/components/ChangeVehicleAlert/ChangeVehicleAlert';
import { PaymentAlert } from '@/components/PaymentAlert/PaymentAlert';
import { OrderSuspendedAlert } from '@/components/OrderSuspendedAlert/OrderSuspendedAlert';
import { OrderCanceledView } from '@/components/OrderCanceledView/OrderCanceledView';
import { getWhatNextDescription } from '@/helpers/staticText';
// --- Loading and Error States ---

const LoadingState = () => (
  <div className="flex min-h-[calc(100vh-250px)] w-full justify-center bg-white">
    <section className="container flex flex-col items-center justify-center gap-2.5 py-[18px]">
      <div className="report-details-container">
        <div className="loading">Ładowanie danych raportu...</div>
      </div>
    </section>
  </div>
);

interface ErrorStateProps {
  message: string;
}

const ErrorState = ({ message }: ErrorStateProps) => (
  <div className="flex min-h-[calc(100vh-250px)] w-full justify-center bg-white">
    <section className="container flex flex-col items-center justify-center gap-2.5 py-[18px]">
      <div className="report-details-container">
        <div className="error">{message}</div>
      </div>
    </section>
  </div>
);

// --- Main Component ---

export const ReportDetails = () => {
  const { id } = useParams<{ id: string }>();
  const [report, setReport] = useState<ApiReport | null>(null);
  const [orderStatus, setOrderStatus] = useState<OrderStatusWithVehicles | null>(
    null,
  );
  const [vehicles, setVehicles] = useState<UserVehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchOrderData = useCallback(async (showLoading = true) => {
    if (!id) return;

    try {
      if (showLoading) setLoading(true);

      const response = await reportService.getPublicOrderStatus(id);

      if (!response.status) {
        throw new Error(
          response.message || 'Nie udało się pobrać danych raportu',
        );
      }

      const statusData = mapOrderTimelineToStatus(response.data);
      setOrderStatus(statusData);

      setVehicles(statusData.vehicles);

      const reportData = mapOrderStatusRA2ToReport(response.data);
      setReport(reportData);

      setError(null);
    } catch (err: unknown) {
      const errorMsg = err instanceof Error ? err.message : 'Nieznany błąd';
      setError(
        'Nie udało się pobrać szczegółów raportu. Spróbuj ponownie później.',
      );

      if (process.env.NODE_ENV === 'development') {
        console.error('Błąd podczas pobierania raportu:', err);
        console.error('Szczegóły błędu:', errorMsg);
      }

      setReport(null);
      setOrderStatus(null);
      setVehicles([]);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchOrderData();
  }, [fetchOrderData]);

  const handleVehicleChange = useCallback(() => {
    // Refresh data after vehicle change (without showing loading spinner)
    fetchOrderData(false);
  }, [fetchOrderData]);

  // Logo i kontakt zależne od marki (brand) zamówienia — z `OrderTimelineDto`.
  // Telefon/e-mail brandu (`brandContact*`) z fallbackiem na kontakt MC, gdy
  // backend ich nie zwróci.
  const brandLogo = getBrandLogo(orderStatus?.brandType);
  const fallbackFavicon = getBrandFavicon();
  const brandFavicon = getBrandFavicon(orderStatus?.brandType);
  useFavicon(brandFavicon, fallbackFavicon);
  const brandContact = buildBrandContact(
    orderStatus?.brandContactPhone,
    orderStatus?.brandContactEmail,
  );

  const renderContent = (): ReactNode => {
    // Loading state
    if (loading) {
      return <LoadingState />;
    }

    // Error state
    if (error || !report) {
      return <ErrorState message={error || 'Nie znaleziono raportu'} />;
    }

    // ORDER_CANCELED - show header + canceled message
    if (isOrderCanceled(orderStatus)) {
      return (
        <>
          <OrderHeader
            orderId={report.id}
            createdAt={report.created_at}
            package_type={report.package_type}
            package_description={report.package_description || ''}
          />
          <OrderCanceledView contactEmail={brandContact.email} />
          <OrderVehicles vehicles={vehicles} orderStatus={orderStatus} />
        </>
      );
    }

    // ORDER_SUSPENDED - show header + suspended alert + change vehicle option
    if (isOrderSuspended(orderStatus)) {
      return (
        <>
          <OrderHeader
            orderId={report.id}
            createdAt={report.created_at}
            package_type={report.package_type}
            package_description={report.package_description || ''}
          />
          <OrderSuspendedAlert
            suspensionMessage={orderStatus?.orderSuspensionMessage}
            showWhatNext={canChangeVehicle(orderStatus) && !!orderStatus?.orderUuid}
          />
          {canChangeVehicle(orderStatus) && orderStatus?.orderUuid && (
            <ChangeVehicleAlert
              onChangeVehicle={handleVehicleChange}
              orderId={orderStatus.orderUuid}
            />
          )}
          <OrderVehicles vehicles={vehicles} orderStatus={orderStatus} />
        </>
      );
    }

    // Normal order flow (in progress, completed, etc.)
    return (
      <>
        <OrderHeader
          orderId={report.id}
          createdAt={report.created_at}
          package_type={report.package_type}
          package_description={report.package_description || ''}
        />
        <OrderTimeline steps={report.steps} currentStep={report.current_step} />

        {/* Payment alert for unpaid orders */}
        {shouldShowPaymentBanner(orderStatus) && (
          <PaymentAlert contactEmail={brandContact.email} />
        )}

        {/* Order body with status description */}
        <OrderBody
          status={report.current_step_status}
          inspectionDate={orderStatus?.inspectionDate}
          reportAccessLinkId={vehicles.find((v) => v.active && !v.archived)?.reportAccessLinkId}
        />

        <OrderVehicles vehicles={vehicles} orderStatus={orderStatus} />

        {getWhatNextDescription(report.current_step_status) && (
          <div className="py-5 border-t border-b border-violet-100 bg-slate-50 my-10">
            <section className="container mx-auto" aria-label="Status dokumentu">
              <h3 className="pb-2 text-base font-bold leading-none text-blue">
                Co dalej?
              </h3>
              <p
                className="mt-2 text-sm leading-5 text-grayText max-md:max-w-full"
                dangerouslySetInnerHTML={{
                  __html: getWhatNextDescription(report.current_step_status) ?? '',
                }}
              ></p>
            </section>
          </div>
        )}

        {/* Change vehicle option */}
        {canChangeVehicle(orderStatus) && orderStatus?.orderUuid && (
          <ChangeVehicleAlert
            onChangeVehicle={handleVehicleChange}
            orderId={orderStatus.orderUuid}
          />
        )}
      </>
    );
  };

  return (
    <Layout
      title="Śledź swoje zamówienie"
      headerLogoSrc={brandLogo}
      footerContent={<Footer logoSrc={brandLogo} contact={brandContact} />}
    >
      {renderContent()}
    </Layout>
  );
};

export default ReportDetails;
